import React, { useState, useEffect } from "react";
import { FaSpinner, FaTimes } from "react-icons/fa";
import { useApartment } from "../../hook/useApartment";

interface ExpenseHistoryData {
  perPropertyMonthly: {
    [propertyName: string]: {
      [month: string]: number;
    };
  };
  monthlyTotalAllProperties: {
    [month: string]: number;
  };
  perPropertyYearlyTotal: {
    [propertyName: string]: number;
  };
  grandTotal: number;
}

interface ExpenseHistoryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string | number;
  apartmentName: string;
}

const ExpenseHistoryPopup: React.FC<ExpenseHistoryPopupProps> = ({
  isOpen,
  onClose,
  apartmentId,
  apartmentName
}) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const { useApartmentExpenseSummary } = useApartment();
  
  const { data, isLoading, error, refetch } = useApartmentExpenseSummary(
    apartmentId, 
    selectedYear
  );

  const expenseData: ExpenseHistoryData = data?.data || {
    perPropertyMonthly: {},
    monthlyTotalAllProperties: {},
    perPropertyYearlyTotal: {},
    grandTotal: 0
  };

  useEffect(() => {
    if (isOpen && apartmentId) {
      refetch();
    }
  }, [isOpen, apartmentId, selectedYear, refetch]);

const currentYear = new Date().getFullYear();
const generateYears = () => {
  const startYear = 2010;
  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

const years = generateYears();

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

  // Get all property names from the data
  const propertyNames = Object.keys(expenseData.perPropertyMonthly || {});
  
  // Month names in order
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg overflow-hidden mx-4 w-full lg:w-3/4 xl:w-2/3 max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-primary/10 text-black gap-2">
          <div className="flex flex-col items-start">
            <h2 className="text-lg font-bold">Expenses History - {apartmentName}</h2>
            <p className="text-sm text-gray-600">Apartment ID: {apartmentId}</p>
            {expenseData.grandTotal > 0 && (
              <p className="text-sm font-semibold text-green-600">
                Yearly Total: ₹{expenseData.grandTotal.toLocaleString()}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-grow sm:flex-grow-0">
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                style={{ paddingRight: "2.5em" }}
                disabled={isLoading}
              >
                {years.map((year) => (
                  <option 
                  key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
            <button
              className="text-xl font-bold hover:text-primary p-2"
              onClick={onClose}
              disabled={isLoading}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <FaSpinner className="animate-spin mr-2" /> Loading expense data...
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              Error loading expense data. Please try again.
            </div>
          ) : propertyNames.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No expense data available for {selectedYear}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead>
                  <tr>
                    <th className="border px-4 py-2 text-left bg-gray-100 sticky left-0 z-10">Month</th>
                    <th className="border px-4 py-2 text-left bg-gray-100 sticky left-0 z-10">Total</th>
                    {/* Dynamic property columns */}
                    {propertyNames.map((propertyName) => (
                      <th key={propertyName} className="border px-4 py-2 text-left bg-gray-100 min-w-[120px]">
                        {propertyName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthNames.map((month, index) => {
                    const monthTotal = expenseData.monthlyTotalAllProperties[month] || 0;
                    
                    return (
                      <tr key={month} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="border px-4 py-2 font-medium sticky left-0 z-10 bg-inherit">
                          {month}
                        </td>
                        <td className="border px-4 py-2 font-bold sticky left-0 z-10 bg-inherit">
                          ₹{monthTotal.toLocaleString()}
                        </td>
                        {/* Dynamic property values */}
                        {propertyNames.map((propertyName) => {
                          const propertyAmount = expenseData.perPropertyMonthly[propertyName]?.[month] || 0;
                          return (
                            <td key={propertyName} className="border px-4 py-2">
                              ₹{propertyAmount.toLocaleString()}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  
                  {/* Yearly totals row */}
                  <tr className="bg-blue-50 font-bold">
                    <td className="border px-4 py-2 sticky left-0 z-10">Yearly Total</td>
                    <td className="border px-4 py-2 sticky left-0 z-10">₹{expenseData.grandTotal.toLocaleString()}</td>
                    {propertyNames.map((propertyName) => {
                      const yearlyTotal = expenseData.perPropertyYearlyTotal[propertyName] || 0;
                      return (
                        <td key={propertyName} className="border px-4 py-2">
                          ₹{yearlyTotal.toLocaleString()}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 bg-gray-50">
          <button
            className="px-4 py-2 bg-primary text-white rounded hover:bg-secondary transition-colors"
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseHistoryPopup;